import axiosInstance from '../axios';
import type {
  PublicTrustContext,
  PublicTrustSnapshot,
  PublicTrustSnapshotResponse,
} from '../types/publicTrust.ts';

const CACHE_TTL_MS = 5 * 60 * 1000;
const CACHE_PREFIX = 'public-trust:v4';

type CacheEntry = {
  savedAt: number;
  snapshot: PublicTrustSnapshot;
};

export type FetchPublicTrustSnapshotParams = {
  context?: PublicTrustContext;
  jurisdictionId?: number;
  limit?: number;
};

function buildCacheKey(params: FetchPublicTrustSnapshotParams): string {
  const context = params.context ?? 'private_project_publish';
  const jurisdiction = params.jurisdictionId ?? 'national';
  const limit = params.limit ?? 4;
  return `${CACHE_PREFIX}:${context}:${jurisdiction}:limit=${limit}`;
}

function readCache(key: string): PublicTrustSnapshot | null {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry;
    if (!parsed?.savedAt || !parsed?.snapshot) return null;
    if (Date.now() - parsed.savedAt > CACHE_TTL_MS) {
      sessionStorage.removeItem(key);
      return null;
    }
    return parsed.snapshot;
  } catch {
    return null;
  }
}

function writeCache(key: string, snapshot: PublicTrustSnapshot): void {
  try {
    const entry: CacheEntry = { savedAt: Date.now(), snapshot };
    sessionStorage.setItem(key, JSON.stringify(entry));
  } catch {
    /* ignore quota */
  }
}

export async function fetchPublicTrustSnapshot(
  params: FetchPublicTrustSnapshotParams = {}
): Promise<PublicTrustSnapshot | null> {
  const cacheKey = buildCacheKey(params);
  const cached = readCache(cacheKey);
  if (cached) return cached;

  const searchParams = new URLSearchParams();
  searchParams.set('context', params.context ?? 'private_project_publish');
  searchParams.set('limit', String(params.limit ?? 4));
  if (params.jurisdictionId != null && params.jurisdictionId > 0) {
    searchParams.set('jurisdictionId', String(params.jurisdictionId));
  }

  try {
    const response = await axiosInstance.get<PublicTrustSnapshotResponse>(
      `/public-trust/snapshot?${searchParams.toString()}`
    );
    const snapshot = response.data?.data;
    if (!snapshot?.stats || !snapshot?.meta || !Array.isArray(snapshot.activity)) {
      return null;
    }
    writeCache(cacheKey, snapshot);
    return snapshot;
  } catch {
    return null;
  }
}
